<template>
  <div class="container py-5 mt-5">
    <LoadingSpinner v-if="loading" text="Chargement des détails de l'offre..." />

    <div v-else-if="!jobOffer" class="text-center">
      <div class="alert alert-warning">
        <i class="fas fa-exclamation-triangle fa-2x mb-3"></i>
        <h4>Offre non trouvée</h4>
        <p>L'offre d'emploi que vous recherchez n'existe pas ou a été supprimée.</p>
        <router-link to="/job-offers" class="btn btn-primary">
          <i class="fas fa-arrow-left"></i> Retour aux offres
        </router-link>
      </div>
    </div>

    <div v-else class="job-offer-details">
      <!-- En-tête de l'offre -->
      <div class="offer-header mb-4 mt-4">
        <div class="offer-header-inner">
          <div class="offer-title-section">
            <h1 class="offer-title">{{ jobOffer.title }}</h1>
            <div class="offer-meta">
              <span class="badge" :class="getStatusBadgeClass()">
                {{ getStatusText() }}
              </span>
              <span class="deadline-info">
                <i class="fas fa-calendar-alt"></i>
                Date limite: {{ formatDate(jobOffer.deadline) }}
              </span>
            </div>
          </div>

          <div class="offer-actions">
            <button
              v-if="jobOffer.pdf_file"
              @click="downloadPdf"
              class="btn btn-outline-light btn-pdf"
              :disabled="downloadingPdf"
            >
              <i class="fas fa-download"></i>
              {{ downloadingPdf ? 'Téléchargement...' : 'Télécharger PDF' }}
            </button>

            <button
              @click="applyNow"
              class="btn btn-apply"
              :disabled="!canApply()"
            >
              <i class="fas fa-paper-plane"></i> Postuler maintenant
            </button>
          </div>
        </div>
      </div>

      <!-- Contenu principal -->
      <div class="row">
        <div class="col-lg-8">
          <!-- Description -->
          <div class="card mb-4">
            <div class="card-header">
              <h3><i class="fas fa-info-circle text-primary"></i> Description du poste</h3>
            </div>
            <div class="card-body">
              <div class="description-content" v-html="jobOffer.description"></div>
            </div>
          </div>

        </div>

        <div class="col-lg-4">
          <!-- Informations rapides -->
          <div class="card mb-4">
            <div class="card-header">
              <h4><i class="fas fa-clock text-primary"></i> Informations rapides</h4>
            </div>
            <div class="card-body">
              <div class="info-item">
                <i class="fas fa-calendar-alt text-primary"></i>
                <div>
                  <strong>Date limite:</strong><br>
                  {{ formatDate(jobOffer.deadline) }}
                </div>
              </div>
              
              <div class="info-item">
                <i class="fas fa-hourglass-half text-primary"></i>
                <div>
                  <strong>Jours restants:</strong><br>
                  {{ getRemainingDays() }} jour(s)
                </div>
              </div>
              
              <div class="info-item">
                <i class="fas fa-toggle-on text-primary"></i>
                <div>
                  <strong>Statut:</strong><br>
                  {{ getStatusText() }}
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="card mb-4">
            <div class="card-header">
              <h4><i class="fas fa-share-alt text-primary"></i> Partager</h4>
            </div>
            <div class="card-body">
              <div class="share-buttons">
                <button @click="shareOnWhatsApp" class="btn btn-success btn-sm w-100 mb-2">
                  <i class="fab fa-whatsapp"></i> WhatsApp
                </button>
                <button @click="shareOnFacebook" class="btn btn-primary btn-sm w-100 mb-2">
                  <i class="fab fa-facebook"></i> Facebook
                </button>
                <button @click="shareOnLinkedIn" class="btn btn-info btn-sm w-100 mb-2">
                  <i class="fab fa-linkedin"></i> LinkedIn
                </button>
                <button @click="shareOnTwitter" class="btn btn-secondary btn-sm w-100">
                  <i class="fab fa-twitter"></i> Twitter
                </button>
              </div>
            </div>
          </div>

          <!-- Navigation -->
          <div class="card">
            <div class="card-header">
              <h4><i class="fas fa-arrow-left text-primary"></i> Navigation</h4>
            </div>
            <div class="card-body">
              <router-link to="/job-offers" class="btn btn-outline-secondary w-100 mb-2">
                <i class="fas fa-list"></i> Toutes les offres
              </router-link>
              <router-link to="/" class="btn btn-outline-primary w-100">
                <i class="fas fa-home"></i> Accueil
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '../components/LoadingSpinner.vue'

export default {
  name: 'JobOfferDetails',
  components: {
    LoadingSpinner
  },
  
  data() {
    return {
      jobOffer: null,
      loading: true,
      downloadingPdf: false
    }
  },
  
  async mounted() {
    await this.loadJobOffer()
  },
  
  methods: {
    async loadJobOffer() {
      try {
        const offerId = this.$route.params.id
      
        
        const response = await this.$http.get(`/jobs/${offerId}`)
        
        // L'API retourne job_offer au lieu de data
        this.jobOffer = response.data.job_offer || response.data.data
        
        if (!this.jobOffer) {
          console.error('No job offer found in response')
          this.jobOffer = null
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'offre:', error)
        console.error('Error response:', error.response)
        if (error.response?.status === 404) {
          this.jobOffer = null
        } else if (error.response?.status === 401) {
          this.$router.push('/login')
        }
      } finally {
        this.loading = false
      }
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    getRemainingDays() {
      if (!this.jobOffer) return 0
      const deadline = new Date(this.jobOffer.deadline)
      const today = new Date()
      const diffTime = deadline - today
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return Math.max(0, diffDays)
    },
    
    isExpired() {
      if (!this.jobOffer) return true
      return new Date(this.jobOffer.deadline) < new Date()
    },
    
    getStatusText() {
      if (!this.jobOffer) return 'Inconnu'
      if (this.jobOffer.is_active && !this.isExpired()) {
        return 'En cours'
      }
      return 'Expirée'
    },
    
    getStatusBadgeClass() {
      if (!this.jobOffer) return 'badge-secondary'
      if (this.jobOffer.is_active && !this.isExpired()) {
        return 'badge-success'
      }
      return 'badge-danger'
    },
    
    canApply() {
      if (!this.jobOffer) return false
      return this.jobOffer.is_active && !this.isExpired()
    },
    
    async downloadPdf() {
      if (!this.jobOffer?.pdf_file) return
      
      this.downloadingPdf = true
      try {
        const response = await this.$http.get(`/storage/${this.jobOffer.pdf_file}`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.jobOffer.pdf_file.split('/').pop())
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Erreur lors du téléchargement:', error)
        alert('Erreur lors du téléchargement du PDF')
      } finally {
        this.downloadingPdf = false
      }
    },
    
    applyNow() {
      if (!localStorage.getItem('token')) {
        this.$router.push('/login')
        return
      }
      this.$router.push(`/jobs/${this.jobOffer.id}/apply`)
    },
    
    shareOnWhatsApp() {
      const text = `Offre d'emploi: ${this.jobOffer.title} - PREFERE PLUS`
      const url = `${window.location.origin}/job-offers/${this.jobOffer.id}`
      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`)
    },
    
    shareOnFacebook() {
      const url = `${window.location.origin}/job-offers/${this.jobOffer.id}`
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)
    },
    
    shareOnLinkedIn() {
      const url = `${window.location.origin}/job-offers/${this.jobOffer.id}`
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`)
    },
    
    shareOnTwitter() {
      const text = `Offre d'emploi: ${this.jobOffer.title}`
      const url = `${window.location.origin}/job-offers/${this.jobOffer.id}`
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`)
    }
  }
}
</script>

<style scoped>
.job-offer-details {
  max-width: 1200px;
  margin: 0 auto;
}

.offer-header {
  background: linear-gradient(135deg, #29b3cd 0%, #bc6a3c 100%);
  color: white;
  padding: 2rem;
  border-radius: 10px;
  margin-bottom: 2rem;
}

.offer-header-inner {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
}

.offer-title-section {
  flex: 1;
  min-width: 0;
}

.offer-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.offer-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.deadline-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.offer-actions {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  flex-shrink: 0;
}

.btn-pdf {
  border: 2px solid rgba(255,255,255,0.8);
  color: white;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  transition: all 0.3s;
}

.btn-pdf:hover {
  background: rgba(255,255,255,0.2);
  color: white;
  border-color: white;
}

.btn-apply {
  background: white;
  color: #29b3cd;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 700;
  font-size: 0.9rem;
  white-space: nowrap;
  transition: all 0.3s;
}

.btn-apply:hover {
  background: #f0f0f0;
  color: #1d9ed9;
}

.btn-apply:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-item i {
  margin-top: 0.25rem;
  min-width: 20px;
}

.description-content {
  line-height: 1.8;
  font-size: 1.1rem;
}

.description-content h1,
.description-content h2,
.description-content h3 {
  color: #333;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.description-content p {
  margin-bottom: 1rem;
}

.description-content ul {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.description-content li {
  margin-bottom: 0.5rem;
}

.share-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .offer-header {
    padding: 1.5rem;
  }

  .offer-header-inner {
    flex-direction: column;
  }

  .offer-title {
    font-size: 1.5rem;
  }

  .offer-actions {
    flex-direction: row;
    width: 100%;
  }

  .offer-actions .btn-pdf,
  .offer-actions .btn-apply {
    flex: 1;
    text-align: center;
    padding: 10px 12px;
    font-size: 0.85rem;
  }
}

@media (max-width: 575px) {
  .offer-header {
    padding: 1.2rem;
  }

  .offer-title {
    font-size: 1.3rem;
  }

  .offer-actions {
    flex-direction: column;
  }

  .offer-actions .btn-pdf,
  .offer-actions .btn-apply {
    width: 100%;
    white-space: normal;
  }

  .description-content {
    font-size: 1rem;
  }
}
</style> 